import { useState, useCallback } from 'react'
import { AdminLayout } from '~/components/AdminLayout'
import { AreaDrawer } from '~/components/map/AreaDrawer'
import {
  useDeliveryZones,
  useDeliveryFeeAreas,
  useCreateDeliveryZone,
  useUpdateDeliveryZone,
  useDeleteDeliveryZone,
  useCreateDeliveryFeeArea,
  useUpdateDeliveryFeeArea,
  useDeleteDeliveryFeeArea,
  type DeliveryZoneFull,
  type DeliveryFeeArea,
  type CreateDeliveryZoneInput,
  type UpdateDeliveryZoneInput,
  type CreateDeliveryFeeAreaInput,
  type UpdateDeliveryFeeAreaInput,
  type DeliveryFeeAreaType,
} from '~/api/admin-delivery-zones'
import 'remixicon/fonts/remixicon.css'
import styles from './delivery-zones.module.css'

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

interface ZoneFormData {
  name: string
  description: string
  boundaryWkt: string
  baseDeliveryFee: number
  minOrderAmount: number
  estimatedMinutesMin: number
  estimatedMinutesMax: number
  isActive?: boolean
}

interface FeeAreaFormData {
  name: string
  description: string
  areaType: DeliveryFeeAreaType
  /** Delivery fee in IDR (0 = free delivery) */
  fee: number
  boundaryWkt: string
  centerLat: string
  centerLng: string
  radiusKm: string
  isActive?: boolean
}

type DeleteTarget =
  | { type: 'zone'; id: string; name: string }
  | { type: 'feeArea'; id: string; name: string }

const emptyZoneForm: ZoneFormData = {
  name: '',
  description: '',
  boundaryWkt: '',
  baseDeliveryFee: 15000,
  minOrderAmount: 50000,
  estimatedMinutesMin: 30,
  estimatedMinutesMax: 60,
}

const emptyFeeAreaForm: FeeAreaFormData = {
  name: '',
  description: '',
  areaType: 'RADIUS',
  fee: 0,
  boundaryWkt: '',
  centerLat: '',
  centerLng: '',
  radiusKm: '3',
}

export function AdminDeliveryZonesPage() {
  const { data: zones, isLoading, isError, error } = useDeliveryZones()
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null)
  const [showZoneModal, setShowZoneModal] = useState(false)
  const [showFeeAreaModal, setShowFeeAreaModal] = useState(false)
  const [editingZone, setEditingZone] = useState<DeliveryZoneFull | null>(null)
  const [editingFeeArea, setEditingFeeArea] = useState<DeliveryFeeArea | null>(null)
  const [zoneForm, setZoneForm] = useState<ZoneFormData>(emptyZoneForm)
  const [feeAreaForm, setFeeAreaForm] = useState<FeeAreaFormData>(emptyFeeAreaForm)
  const [modalError, setModalError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)

  const { data: feeAreas } = useDeliveryFeeAreas(selectedZoneId ?? '', !!selectedZoneId)

  const createZoneMutation = useCreateDeliveryZone()
  const updateZoneMutation = useUpdateDeliveryZone()
  const deleteZoneMutation = useDeleteDeliveryZone()
  const createFeeAreaMutation = useCreateDeliveryFeeArea()
  const updateFeeAreaMutation = useUpdateDeliveryFeeArea()
  const deleteFeeAreaMutation = useDeleteDeliveryFeeArea()

  const selectedZone = zones?.find(z => z.id === selectedZoneId)

  const handleZoneFormChange = useCallback((field: keyof ZoneFormData, value: string | number | boolean) => {
    setZoneForm(prev => ({ ...prev, [field]: value }))
    setModalError(null)
  }, [])

  const handleFeeAreaFormChange = useCallback((field: keyof FeeAreaFormData, value: string | boolean | number) => {
    setFeeAreaForm(prev => ({ ...prev, [field]: value }))
    setModalError(null)
  }, [])

  const openCreateZoneModal = useCallback(() => {
    setEditingZone(null)
    setZoneForm(emptyZoneForm)
    setModalError(null)
    setShowZoneModal(true)
  }, [])

  const openEditZoneModal = useCallback((zone: DeliveryZoneFull) => {
    setEditingZone(zone)
    setZoneForm({
      name: zone.name,
      description: zone.description ?? '',
      boundaryWkt: zone.boundaryWkt ?? '',
      baseDeliveryFee: zone.baseDeliveryFee,
      minOrderAmount: zone.minOrderAmount,
      estimatedMinutesMin: zone.estimatedMinutesMin,
      estimatedMinutesMax: zone.estimatedMinutesMax,
      isActive: zone.isActive,
    })
    setModalError(null)
    setShowZoneModal(true)
  }, [])

  const openCreateFeeAreaModal = useCallback(() => {
    setEditingFeeArea(null)
    setFeeAreaForm(emptyFeeAreaForm)
    setModalError(null)
    setShowFeeAreaModal(true)
  }, [])

  const openEditFeeAreaModal = useCallback((area: DeliveryFeeArea) => {
    setEditingFeeArea(area)
    setFeeAreaForm({
      name: area.name,
      description: area.description ?? '',
      areaType: area.areaType,
      fee: area.fee,
      boundaryWkt: area.boundaryWkt ?? '',
      centerLat: area.centerLat?.toString() ?? '',
      centerLng: area.centerLng?.toString() ?? '',
      radiusKm: area.radiusKm?.toString() ?? '',
      isActive: area.isActive,
    })
    setModalError(null)
    setShowFeeAreaModal(true)
  }, [])

  const handleSaveZone = useCallback(async () => {
    if (!zoneForm.name) return

    if (editingZone) {
      const input: UpdateDeliveryZoneInput = {
        name: zoneForm.name,
        description: zoneForm.description || undefined,
        baseDeliveryFee: zoneForm.baseDeliveryFee,
        minOrderAmount: zoneForm.minOrderAmount,
        estimatedMinutesMin: zoneForm.estimatedMinutesMin,
        estimatedMinutesMax: zoneForm.estimatedMinutesMax,
        isActive: zoneForm.isActive,
      }
      if (zoneForm.boundaryWkt) {
        input.boundaryWkt = zoneForm.boundaryWkt
      }
      await updateZoneMutation.mutateAsync({ id: editingZone.id, input })
    } else {
      if (!zoneForm.boundaryWkt) {
        setModalError('Boundary WKT is required for new zones.')
        return
      }
      const input: CreateDeliveryZoneInput = {
        name: zoneForm.name,
        description: zoneForm.description || undefined,
        boundaryWkt: zoneForm.boundaryWkt,
        baseDeliveryFee: zoneForm.baseDeliveryFee,
        minOrderAmount: zoneForm.minOrderAmount,
        estimatedMinutesMin: zoneForm.estimatedMinutesMin,
        estimatedMinutesMax: zoneForm.estimatedMinutesMax,
      }
      await createZoneMutation.mutateAsync(input)
    }
    setShowZoneModal(false)
  }, [zoneForm, editingZone, updateZoneMutation, createZoneMutation])

  const handleDeleteZone = useCallback((zone: DeliveryZoneFull) => {
    setDeleteTarget({ type: 'zone', id: zone.id, name: zone.name })
  }, [])

  const handleSaveFeeArea = useCallback(async () => {
    if (!selectedZoneId || !feeAreaForm.name) return

    if (editingFeeArea) {
      const input: UpdateDeliveryFeeAreaInput = {
        name: feeAreaForm.name,
        description: feeAreaForm.description || undefined,
        fee: feeAreaForm.fee,
        isActive: feeAreaForm.isActive,
      }
      if (feeAreaForm.areaType === 'POLYGON' && feeAreaForm.boundaryWkt) {
        input.boundaryWkt = feeAreaForm.boundaryWkt
      }
      if (feeAreaForm.areaType === 'RADIUS') {
        if (feeAreaForm.centerLat) input.centerLat = parseFloat(feeAreaForm.centerLat)
        if (feeAreaForm.centerLng) input.centerLng = parseFloat(feeAreaForm.centerLng)
        if (feeAreaForm.radiusKm) input.radiusKm = parseFloat(feeAreaForm.radiusKm)
      }
      await updateFeeAreaMutation.mutateAsync({ id: editingFeeArea.id, input })
    } else {
      const input: CreateDeliveryFeeAreaInput = {
        zoneId: selectedZoneId,
        name: feeAreaForm.name,
        description: feeAreaForm.description || undefined,
        areaType: feeAreaForm.areaType,
        fee: feeAreaForm.fee,
      }
      if (feeAreaForm.areaType === 'POLYGON') {
        if (!feeAreaForm.boundaryWkt) {
          setModalError('Boundary WKT is required for polygon areas.')
          return
        }
        input.boundaryWkt = feeAreaForm.boundaryWkt
      } else {
        if (!feeAreaForm.centerLat || !feeAreaForm.centerLng || !feeAreaForm.radiusKm) {
          setModalError('Center coordinates and radius are required for radius areas.')
          return
        }
        input.centerLat = parseFloat(feeAreaForm.centerLat)
        input.centerLng = parseFloat(feeAreaForm.centerLng)
        input.radiusKm = parseFloat(feeAreaForm.radiusKm)
      }
      await createFeeAreaMutation.mutateAsync(input)
    }
    setShowFeeAreaModal(false)
  }, [feeAreaForm, selectedZoneId, editingFeeArea, updateFeeAreaMutation, createFeeAreaMutation])

  const handleDeleteFeeArea = useCallback((area: DeliveryFeeArea) => {
    if (!selectedZoneId) return
    setDeleteTarget({ type: 'feeArea', id: area.id, name: area.name })
  }, [selectedZoneId])

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return

    if (deleteTarget.type === 'zone') {
      await deleteZoneMutation.mutateAsync(deleteTarget.id)
      if (selectedZoneId === deleteTarget.id) {
        setSelectedZoneId(null)
      }
    } else if (selectedZoneId) {
      await deleteFeeAreaMutation.mutateAsync({ id: deleteTarget.id, zoneId: selectedZoneId })
    }

    setDeleteTarget(null)
  }, [deleteTarget, deleteZoneMutation, deleteFeeAreaMutation, selectedZoneId])

  if (isLoading) {
    return (
      <AdminLayout title="DELIVERY ZONES">
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Loading zones...</span>
        </div>
      </AdminLayout>
    )
  }

  if (isError) {
    return (
      <AdminLayout title="DELIVERY ZONES">
        <div className={styles.error}>
          Error: {error?.message || 'Failed to load delivery zones'}
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout title="DELIVERY ZONES">
      <div className={styles.page}>
        {/* Zones List Panel */}
        <section className={styles.zonesPanel}>
          <div className={styles.panelHeader}>
            <h3>ZONES</h3>
            <button className={styles.addButton} onClick={openCreateZoneModal}>
              <i className="ri-add-line" /> ADD ZONE
            </button>
          </div>
          <div className={styles.zonesList}>
            {zones?.map(zone => (
              <div
                key={zone.id}
                className={`${styles.zoneCard} ${selectedZoneId === zone.id ? styles.zoneCardSelected : ''} ${!zone.isActive ? styles.zoneCardInactive : ''}`}
                onClick={() => setSelectedZoneId(zone.id)}
              >
                <div className={styles.zoneInfo}>
                  <span className={styles.zoneName}>{zone.name}</span>
                  <span className={styles.zoneDetails}>
                    {formatCurrency(zone.baseDeliveryFee)} • {zone.estimatedMinutesMin}-{zone.estimatedMinutesMax} min
                  </span>
                </div>
                <div className={styles.zoneActions}>
                  <button
                    className={styles.iconButton}
                    onClick={(e) => { e.stopPropagation(); openEditZoneModal(zone) }}
                    title="Edit zone"
                  >
                    <i className="ri-pencil-line" />
                  </button>
                  <button
                    className={`${styles.iconButton} ${styles.deleteButton}`}
                    onClick={(e) => { e.stopPropagation(); handleDeleteZone(zone) }}
                    title="Delete zone"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
                {!zone.isActive && <span className={styles.inactiveLabel}>INACTIVE</span>}
              </div>
            ))}
            {(!zones || zones.length === 0) && (
              <div className={styles.emptyState}>
                <i className="ri-map-2-line" />
                <span>No delivery zones configured</span>
              </div>
            )}
          </div>
        </section>

        {/* Delivery Fee Areas Panel */}
        <section className={styles.areasPanel}>
          {selectedZone ? (
            <>
              <div className={styles.panelHeader}>
                <div>
                  <h3>DELIVERY FEE AREAS</h3>
                  <span className={styles.subtitle}>in {selectedZone.name}</span>
                </div>
                <button className={styles.addButton} onClick={openCreateFeeAreaModal}>
                  <i className="ri-add-line" /> ADD AREA
                </button>
              </div>
              <div className={styles.areasList}>
                {feeAreas?.map(area => (
                  <div
                    key={area.id}
                    className={`${styles.areaCard} ${!area.isActive ? styles.areaCardInactive : ''}`}
                  >
                    <div className={styles.areaInfo}>
                      <span className={styles.areaName}>{area.name}</span>
                      <span className={styles.areaDetails}>
                        {area.fee === 0 ? (
                          <><i className="ri-gift-line" /> FREE</>
                        ) : (
                          <>{formatCurrency(area.fee)}</>
                        )}
                        {' • '}
                        {area.areaType === 'RADIUS' ? (
                          <>Radius: {area.radiusKm} km</>
                        ) : (
                          <>Polygon</>
                        )}
                      </span>
                    </div>
                    <div className={styles.areaActions}>
                      <button
                        className={styles.iconButton}
                        onClick={() => openEditFeeAreaModal(area)}
                        title="Edit area"
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        className={`${styles.iconButton} ${styles.deleteButton}`}
                        onClick={() => handleDeleteFeeArea(area)}
                        title="Delete area"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                    {!area.isActive && <span className={styles.inactiveLabel}>INACTIVE</span>}
                  </div>
                ))}
                {(!feeAreas || feeAreas.length === 0) && (
                  <div className={styles.emptyState}>
                    <i className="ri-gift-2-line" />
                    <span>No delivery fee areas in this zone</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={styles.selectZonePrompt}>
              <i className="ri-arrow-left-line" />
              <span>Select a zone to manage delivery fee areas</span>
            </div>
          )}
        </section>
      </div>

      {/* Zone Modal */}
      {showZoneModal && (
        <div className={styles.modalOverlay} onClick={() => setShowZoneModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editingZone ? 'EDIT ZONE' : 'CREATE ZONE'}</h3>
              <button className={styles.closeButton} onClick={() => setShowZoneModal(false)}>
                <i className="ri-close-line" />
              </button>
            </div>
            <div className={styles.modalBody}>
              {modalError && (
                <div className={styles.inlineError}>{modalError}</div>
              )}
              <div className={styles.formGroup}>
                <label>Zone Name *</label>
                <input
                  type="text"
                  value={zoneForm.name}
                  onChange={e => handleZoneFormChange('name', e.target.value)}
                  placeholder="e.g., Sampit Metro"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                  value={zoneForm.description}
                  onChange={e => handleZoneFormChange('description', e.target.value)}
                  placeholder="Optional description"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Zone Boundary {!editingZone && '*'}</label>
                <AreaDrawer
                  mode="polygon"
                  boundaryWkt={zoneForm.boundaryWkt}
                  onBoundaryChange={(wkt) => handleZoneFormChange('boundaryWkt', wkt)}
                  initialCenter={[112.95, -2.55]}
                  initialZoom={11}
                />
                {editingZone && !zoneForm.boundaryWkt && (
                  <span className={styles.hint}>Draw new boundary or leave unchanged</span>
                )}
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Base Delivery Fee (IDR)</label>
                  <input
                    type="number"
                    value={zoneForm.baseDeliveryFee}
                    onChange={e => handleZoneFormChange('baseDeliveryFee', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Min Order Amount (IDR)</label>
                  <input
                    type="number"
                    value={zoneForm.minOrderAmount}
                    onChange={e => handleZoneFormChange('minOrderAmount', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Est. Min Time (min)</label>
                  <input
                    type="number"
                    value={zoneForm.estimatedMinutesMin}
                    onChange={e => handleZoneFormChange('estimatedMinutesMin', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Est. Max Time (min)</label>
                  <input
                    type="number"
                    value={zoneForm.estimatedMinutesMax}
                    onChange={e => handleZoneFormChange('estimatedMinutesMax', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
              {editingZone && (
                <div className={styles.toggleGroup}>
                  <label>Active</label>
                  <button
                    type="button"
                    className={`${styles.toggle} ${zoneForm.isActive ? styles.toggleOn : ''}`}
                    onClick={() => handleZoneFormChange('isActive', !zoneForm.isActive)}
                  >
                    <span className={styles.toggleKnob} />
                  </button>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelButton} onClick={() => setShowZoneModal(false)}>
                CANCEL
              </button>
              <button
                className={styles.saveButton}
                onClick={handleSaveZone}
                disabled={createZoneMutation.isPending || updateZoneMutation.isPending}
              >
                {(createZoneMutation.isPending || updateZoneMutation.isPending) ? 'SAVING...' : 'SAVE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Free Area Modal */}
      {showFeeAreaModal && (
        <div className={styles.modalOverlay} onClick={() => setShowFeeAreaModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editingFeeArea ? 'EDIT FEE AREA' : 'CREATE FEE AREA'}</h3>
              <button className={styles.closeButton} onClick={() => setShowFeeAreaModal(false)}>
                <i className="ri-close-line" />
              </button>
            </div>
            <div className={styles.modalBody}>
              {modalError && (
                <div className={styles.inlineError}>{modalError}</div>
              )}
              <div className={styles.formGroup}>
                <label>Area Name *</label>
                <input
                  type="text"
                  value={feeAreaForm.name}
                  onChange={e => handleFeeAreaFormChange('name', e.target.value)}
                  placeholder="e.g., Baamang Tengah"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                  value={feeAreaForm.description}
                  onChange={e => handleFeeAreaFormChange('description', e.target.value)}
                  placeholder="Optional description"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Delivery Fee (IDR)</label>
                <input
                  type="number"
                  value={feeAreaForm.fee}
                  onChange={e => handleFeeAreaFormChange('fee', parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
                <span className={styles.hint}>Set to 0 for free delivery</span>
              </div>
              {!editingFeeArea && (
                <div className={styles.formGroup}>
                  <label>Area Type *</label>
                  <div className={styles.radioGroup}>
                    <label>
                      <input
                        type="radio"
                        name="areaType"
                        checked={feeAreaForm.areaType === 'RADIUS'}
                        onChange={() => handleFeeAreaFormChange('areaType', 'RADIUS')}
                      />
                      Radius (circle)
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="areaType"
                        checked={feeAreaForm.areaType === 'POLYGON'}
                        onChange={() => handleFeeAreaFormChange('areaType', 'POLYGON')}
                      />
                      Polygon (custom shape)
                    </label>
                  </div>
                </div>
              )}
              {feeAreaForm.areaType === 'RADIUS' ? (
                <>
                  <div className={styles.formGroup}>
                    <label>Delivery Fee Area (Click to set center, drag to move)</label>
                    <AreaDrawer
                      mode="radius"
                      center={feeAreaForm.centerLat && feeAreaForm.centerLng ? {
                        lat: parseFloat(feeAreaForm.centerLat),
                        lng: parseFloat(feeAreaForm.centerLng),
                      } : undefined}
                      radiusKm={parseFloat(feeAreaForm.radiusKm) || 3}
                      onCenterChange={(center) => {
                        handleFeeAreaFormChange('centerLat', center.lat.toFixed(6))
                        handleFeeAreaFormChange('centerLng', center.lng.toFixed(6))
                      }}
                      onRadiusChange={(radius) => handleFeeAreaFormChange('radiusKm', radius.toString())}
                      initialCenter={[112.95, -2.55]}
                      initialZoom={12}
                    />
                  </div>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>Center Latitude</label>
                      <input
                        type="text"
                        value={feeAreaForm.centerLat}
                        onChange={e => handleFeeAreaFormChange('centerLat', e.target.value)}
                        placeholder="-2.5432"
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Center Longitude</label>
                      <input
                        type="text"
                        value={feeAreaForm.centerLng}
                        onChange={e => handleFeeAreaFormChange('centerLng', e.target.value)}
                        placeholder="112.9421"
                      />
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Radius (km)</label>
                    <input
                      type="text"
                      value={feeAreaForm.radiusKm}
                      onChange={e => handleFeeAreaFormChange('radiusKm', e.target.value)}
                      placeholder="3"
                    />
                  </div>
                </>
              ) : (
                <div className={styles.formGroup}>
                  <label>Delivery Fee Area (Draw polygon)</label>
                  <AreaDrawer
                    mode="polygon"
                    boundaryWkt={feeAreaForm.boundaryWkt}
                    onBoundaryChange={(wkt) => handleFeeAreaFormChange('boundaryWkt', wkt)}
                    initialCenter={[112.95, -2.55]}
                    initialZoom={12}
                  />
                </div>
              )}
              {editingFeeArea && (
                <div className={styles.toggleGroup}>
                  <label>Active</label>
                  <button
                    type="button"
                    className={`${styles.toggle} ${feeAreaForm.isActive ? styles.toggleOn : ''}`}
                    onClick={() => handleFeeAreaFormChange('isActive', !feeAreaForm.isActive)}
                  >
                    <span className={styles.toggleKnob} />
                  </button>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelButton} onClick={() => setShowFeeAreaModal(false)}>
                CANCEL
              </button>
              <button
                className={styles.saveButton}
                onClick={handleSaveFeeArea}
                disabled={createFeeAreaMutation.isPending || updateFeeAreaMutation.isPending}
              >
                {(createFeeAreaMutation.isPending || updateFeeAreaMutation.isPending) ? 'SAVING...' : 'SAVE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className={styles.modalOverlay} onClick={() => setDeleteTarget(null)}>
          <div
            className={`${styles.modal} ${styles.confirmModal}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-delivery-zone-title"
            onClick={e => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3 id="delete-delivery-zone-title">
                DELETE {deleteTarget.type === 'zone' ? 'ZONE' : 'FEE AREA'}
              </h3>
              <button className={styles.closeButton} onClick={() => setDeleteTarget(null)}>
                <i className="ri-close-line" />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.confirmBody}>
                <span className={styles.confirmEyebrow}>DESTRUCTIVE ACTION</span>
                <p>
                  Delete <strong>{deleteTarget.name}</strong>? This removes it from delivery calculations for future orders.
                </p>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelButton} onClick={() => setDeleteTarget(null)}>
                CANCEL
              </button>
              <button
                className={styles.dangerButton}
                onClick={confirmDelete}
                disabled={deleteZoneMutation.isPending || deleteFeeAreaMutation.isPending}
              >
                {(deleteZoneMutation.isPending || deleteFeeAreaMutation.isPending) ? 'DELETING...' : 'DELETE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
